/**
 * "New canvas" opens the page shell first and lets the create request finish
 * behind it: a pending tab appears in the strip on click and the route moves
 * to {@link ROUTES.CANVAS.NEW}, which renders the same loader the canvas page
 * shows while its room connects.
 *
 * The canvas id is minted server-side, and the same request writes the SpiceDB
 * relationships and creates the Liveblocks room, so nothing here may reach the
 * room ahead of the response: no client-side id, no token prefetch, no early
 * RoomProvider. The shell is chrome and a spinner; the real canvas route only
 * mounts once the response has landed.
 */

import {
  DEFAULT_CANVAS_NAME,
  useCreateCanvas,
  type CreateCanvasVariables,
} from "@/api/canvas";
import { ROUTES } from "@/constants/routes";
import { useOpenTabs } from "@/context/OpenTabsContext";
import type { Canvas } from "@cohesive/api-client";
import type { AxiosError } from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface StartCanvasCreationOptions {
  /** Runs in addition to the shell's own error state (rail keeps its toast). */
  onError?: (error: AxiosError) => void;
  onSuccess?: (canvas: Canvas) => void;
}

/**
 * `shown` is set once the shell has rendered a settled outcome. It is what
 * lets the provider tell an outcome the route has moved on from apart from
 * one the shell has not reached yet — the shell's own navigation can commit
 * after a fast response lands.
 */
type CreationPhase =
  | { status: "idle" }
  | { status: "creating" }
  | { status: "created"; canvasId: string; shown?: true }
  | { status: "error"; error: AxiosError; shown?: true };

function isSettled(
  phase: CreationPhase,
): phase is Extract<CreationPhase, { shown?: true }> {
  return phase.status === "created" || phase.status === "error";
}

interface CanvasCreationContextValue {
  phase: CreationPhase;
  isCreating: boolean;
  startCanvasCreation: (
    variables: CreateCanvasVariables,
    options?: StartCanvasCreationOptions,
  ) => void;
  /** Re-runs the create the shell is showing an error for. */
  retryCanvasCreation: () => void;
  /** Leaves the shell without retrying. */
  dismissCanvasCreation: () => void;
  /** The shell has rendered the current outcome. */
  markCanvasCreationShown: () => void;
}

const CanvasCreationContext = createContext<CanvasCreationContextValue | null>(
  null,
);

export function CanvasCreationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onShell = pathname === ROUTES.CANVAS.NEW;
  // The callbacks below fire long after the click; they need the route as it
  // is then, not as it was.
  const onShellRef = useRef(onShell);
  onShellRef.current = onShell;
  const {
    addPendingCanvasTab,
    resolvePendingCanvasTab,
    removePendingCanvasTab,
  } = useOpenTabs();
  const [phase, setPhase] = useState<CreationPhase>({ status: "idle" });
  const lastAttempt = useRef<{
    variables: CreateCanvasVariables;
    options?: StartCanvasCreationOptions;
  } | null>(null);

  // `isPending` is state and lands a render late, so it cannot stop a second
  // call in the same tick on its own. Cleared from the hook-level onSettled:
  // query-core runs that from the mutation itself, where the callbacks passed
  // to mutate() are dropped if this observer is gone by the time it settles.
  const inFlight = useRef(false);
  const createCanvas = useCreateCanvas({
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // An outcome is spent once the shell has shown it and the route has moved
  // on — the shell's own Navigate landed, or the user left the error. Keyed on
  // that pair rather than on the shell unmounting: StrictMode's simulated
  // unmount would reset a `created` the shell mounted into and send it home.
  // And not on the route alone: a fast response can settle before the shell's
  // own navigation commits, and the outcome has to survive until it does.
  useEffect(() => {
    if (onShell) return;
    setPhase((current) =>
      isSettled(current) && current.shown ? { status: "idle" } : current,
    );
  }, [onShell, phase]);

  const markShown = useCallback(() => {
    setPhase((current) =>
      isSettled(current) && !current.shown
        ? { ...current, shown: true }
        : current,
    );
  }, []);

  const start = useCallback(
    (
      variables: CreateCanvasVariables,
      options?: StartCanvasCreationOptions,
    ) => {
      if (inFlight.current) return;
      inFlight.current = true;
      lastAttempt.current = { variables, options };

      const pendingTabId = addPendingCanvasTab(
        variables.name?.trim() || DEFAULT_CANVAS_NAME,
      );
      setPhase({ status: "creating" });

      // Kicked off before the route swap: the request leaves on the microtask
      // that follows, ahead of the render that unmounts the outgoing canvas
      // page (React Flow and its Liveblocks room). Navigating first would
      // queue the create behind that work.
      createCanvas.mutate(variables, {
        onSuccess: (canvas) => {
          resolvePendingCanvasTab(pendingTabId, {
            kind: "canvas",
            id: canvas.id,
            name: canvas.name || DEFAULT_CANVAS_NAME,
          });
          // The shell navigates itself off this phase. Setting the phase and
          // calling navigate from the same handler races: the router can defer
          // its update through startTransition while the phase commits at once,
          // and the still-mounted shell would read the reset as "landed here
          // directly" and bounce home.
          setPhase({ status: "created", canvasId: canvas.id });
          options?.onSuccess?.(canvas);
        },
        onError: (error) => {
          removePendingCanvasTab(pendingTabId);
          setPhase({ status: "error", error });
          options?.onError?.(error);
        },
      });

      // A retry is already standing on the shell; re-navigating would only add
      // a history entry.
      if (!onShellRef.current) navigate(ROUTES.CANVAS.NEW);
    },
    [
      createCanvas,
      addPendingCanvasTab,
      resolvePendingCanvasTab,
      removePendingCanvasTab,
      navigate,
    ],
  );

  const retry = useCallback(() => {
    const attempt = lastAttempt.current;
    if (!attempt) return;
    start(attempt.variables, attempt.options);
  }, [start]);

  // Leaving the route is what clears the shown error, see above.
  const dismiss = useCallback(() => navigate(ROUTES.CANVAS.ROOT), [navigate]);

  const value = useMemo<CanvasCreationContextValue>(
    () => ({
      phase,
      isCreating: createCanvas.isPending,
      startCanvasCreation: start,
      retryCanvasCreation: retry,
      dismissCanvasCreation: dismiss,
      markCanvasCreationShown: markShown,
    }),
    [phase, createCanvas.isPending, start, retry, dismiss, markShown],
  );

  return (
    <CanvasCreationContext.Provider value={value}>
      {children}
    </CanvasCreationContext.Provider>
  );
}

export function useCanvasCreation(): CanvasCreationContextValue {
  const ctx = useContext(CanvasCreationContext);
  if (!ctx) {
    throw new Error(
      "useCanvasCreation must be used within a CanvasCreationProvider",
    );
  }
  return ctx;
}
